import { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { AlertTriangle, Clock } from 'lucide-react';
import { ytQuotaQueryOptions } from '@/lib/youtube-admin';

/** Top-of-page strip showing today's YouTube Data API quota usage.
 *
 * Three states:
 *   - <75% → quiet emerald strip (just informational)
 *   - 75-95% → amber warning
 *   - ≥95% → red alarm + explicit reset countdown
 *
 * Hidden entirely if the snapshot request fails so a momentary KV
 * blip doesn't push a confusing error into the operator's view of
 * the playlists grid. */
export function QuotaBanner() {
  const { data, isLoading } = useQuery(ytQuotaQueryOptions);

  if (isLoading || !data) return null;

  const { used, limit, percent, resetAt } = data;
  const tone: 'ok' | 'warn' | 'danger' = percent >= 95 ? 'danger' : percent >= 75 ? 'warn' : 'ok';

  const styles =
    tone === 'danger'
      ? 'border-red-300 bg-red-50 text-red-900 dark:border-red-800 dark:bg-red-900/30 dark:text-red-100'
      : tone === 'warn'
        ? 'border-amber-300 bg-amber-50 text-amber-900 dark:border-amber-800 dark:bg-amber-900/30 dark:text-amber-100'
        : 'border-emerald-200 bg-emerald-50 text-emerald-800 dark:border-emerald-800 dark:bg-emerald-900/20 dark:text-emerald-200';

  const barFill =
    tone === 'danger'
      ? 'bg-red-500'
      : tone === 'warn'
        ? 'bg-amber-500'
        : 'bg-emerald-500 dark:bg-emerald-400';

  return (
    <div className={`rounded-lg border px-3 py-2 text-xs ${styles}`}>
      <div className="flex items-center gap-2">
        {tone !== 'ok' && <AlertTriangle className="h-3.5 w-3.5 shrink-0" />}
        <span className="font-medium">
          {tone === 'danger'
            ? 'YouTube API quota เกือบเต็ม — cron จะหยุดดึงข้อมูลใหม่จนกว่าจะ reset'
            : tone === 'warn'
              ? 'YouTube API quota ใช้ไปมาก — ระวัง'
              : 'YouTube API quota วันนี้'}
        </span>
        <span className="ml-auto font-mono tabular-nums">
          {used.toLocaleString()} / {limit.toLocaleString()} ({percent}%)
        </span>
      </div>
      <div className="mt-1.5 h-1.5 overflow-hidden rounded-full bg-black/10 dark:bg-white/10">
        <div
          className={`h-full transition-[width] duration-500 ${barFill}`}
          style={{ width: `${Math.min(100, percent)}%` }}
        />
      </div>
      <div className="mt-1 flex items-center gap-1 text-[10px] opacity-80">
        <Clock className="h-3 w-3" />
        <span>
          reset <ResetCountdown iso={resetAt} />
        </span>
        <span className="opacity-60">(เที่ยงคืน Pacific = ~14:00 เวลาไทย)</span>
      </div>
    </div>
  );
}

/** Live "in 4h 12m" countdown — recomputed each second client-side from
 *  the server-supplied ISO. Avoid re-fetching just for the clock tick.  */
function ResetCountdown({ iso }: { iso: string }) {
  const target = new Date(iso).getTime();
  const [remaining, setRemaining] = useState(() => target - Date.now());
  useEffect(() => {
    const t = setInterval(() => setRemaining(target - Date.now()), 1000);
    return () => clearInterval(t);
  }, [target]);
  if (remaining <= 0) return <span>เร็วๆ นี้</span>;
  const totalMin = Math.floor(remaining / 60_000);
  const h = Math.floor(totalMin / 60);
  const m = totalMin % 60;
  if (h === 0) return <span>ใน {m} นาที</span>;
  return (
    <span>
      ใน {h} ชม. {m} นาที
    </span>
  );
}
